import React, { useState, useEffect } from "react";
import EditIcon from "../../../../ui/EditIcon";
import {
  Table,
  Form,
  Select,
  Input,
  Button,
  Space,
  Modal,
  InputNumber,
  Typography,
  Tag,
} from "antd";
import inputStyles from "../../../../../styles/Custom/Input.module.css";
import { getDietApi } from "../../../../../services/diet.services";
// import { EyeOutlined } from "@ant-design/icons";
import { customTag } from "../nutritionistKeys";
import useIsMobile from "../../../../../hooks/useIsMobile";
import "./DietEditTable.css";

const getUnits = (key) =>
  ["proteins", "carbohydrates"]?.includes(key)
    ? "g"
    : key === "calories"
    ? "kcal"
    : "mg";

const DietEditTable = ({
  type,
  data,
  form,
  onChange,
  titleData,
  dietType,
  dietDetails,
  combinedTotals,
}) => {
  const isMobile = useIsMobile();
  const disabled = !dietType;

  const [items, setItems] = useState([]);
  const [tableData, setTableData] = useState(data);
  // const [hiddenRows, setHiddenRows] = useState(new Set()); // State to track hidden rows
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [modalForm] = Form.useForm();
  const [currentModalNutrition, setCurrentModalNutrition] = useState({
    proteins: 0,
    calories: 0,
    sodium: 0,
    potassium: 0,
    carbohydrates: 0,
  });

  useEffect(() => {
    getDietApi(`patination=no_pagination`)
      .then((res) => setItems(res?.data?.data || []))
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    // Set initial form values based on data
    tableData?.forEach((record) => {
      form.setFieldsValue({
        [record.time]: {
          items: record?.items?.map((i) => i.diet__id),
          comments: record?.comments || "",
        },
      });
    });
  }, [tableData]);

  useEffect(() => {
    if (editingItem) {
      const selectedItemIds =
        editingItem.items?.map((item) => item.diet__id) || [];
      modalForm.setFieldsValue({
        selectedItems: selectedItemIds,
        items: editingItem.items || [],
      });
      // Calculate initial nutrition values for the modal
      calculateModalNutrition(editingItem.items || []);
    }
  }, [editingItem]);

  // Function to calculate total daily nutrition values including all meals
  const calculateModalNutrition = (currentEditingItems) => {
    // Calculate nutrition for all meals in the day
    const dailyTotalValues = tableData.reduce(
      (dayTotals, mealRecord) => {
        // If this is the meal being edited, use the current editing items
        const itemsToUse =
          mealRecord.time === editingItem?.time
            ? currentEditingItems
            : mealRecord.items || [];

        // Calculate nutrition for this meal
        const mealTotals = itemsToUse.reduce(
          (mealTotals, { diet__id, quantity }) => {
            const dietItem = items.find((item) => item.id === diet__id);
            if (dietItem) {
              mealTotals.proteins += (dietItem.protien || 0) * (quantity || 1);
              mealTotals.calories += (dietItem.calories || 0) * (quantity || 1);
              mealTotals.sodium += (dietItem.sodium || 0) * (quantity || 1);
              mealTotals.potassium +=
                (dietItem.potassium || 0) * (quantity || 1);
              mealTotals.carbohydrates +=
                (dietItem.carbohydrates || 0) * (quantity || 1);
            }
            return mealTotals;
          },
          {
            proteins: 0,
            calories: 0,
            sodium: 0,
            potassium: 0,
            carbohydrates: 0,
          }
        );

        // Add this meal's totals to the daily totals
        dayTotals.proteins += mealTotals.proteins;
        dayTotals.calories += mealTotals.calories;
        dayTotals.sodium += mealTotals.sodium;
        dayTotals.potassium += mealTotals.potassium;
        dayTotals.carbohydrates += mealTotals.carbohydrates;

        return dayTotals;
      },
      {
        proteins: 0,
        calories: 0,
        sodium: 0,
        potassium: 0,
        carbohydrates: 0,
      }
    );

    setCurrentModalNutrition(dailyTotalValues);
  };

  const handleItemChange = (selectedItems, record) => {
    // const formValues = form.getFieldsValue(); // Get all current form values

    const totalValues = selectedItems.reduce(
      (totals, itemId) => {
        const item = items?.find((dietItem) => dietItem?.id === itemId);
        if (item) {
          totals.proteins += item?.protien || 0;
          totals.calories += item?.calories || 0;
          totals.sodium += item?.sodium || 0;
          totals.potassium += item?.potassium || 0;
          totals.carbohydrates += item?.carbohydrates || 0;
        }
        return totals;
      },
      { proteins: 0, calories: 0, sodium: 0, potassium: 0, carbohydrates: 0 }
    );

    const updatedData = tableData?.map((data) => {
      const timeKey = data.time;
      const currentFormValue = record || {};
      return timeKey === record.time
        ? {
            ...data,
            items: selectedItems?.map((i) => ({
              diet__id: i,
              quantity:
                data?.items?.find((item) => item?.diet__id === i)?.quantity ??
                1,
              comments: "",
            })),
            // comments: currentFormValue.comments || "", // current field's comment
            ...totalValues,
            modified: true,
          }
        : {
            ...data,
            items: data.items,
            comments: data.comments,
          };
    });

    // Set updated form values explicitly
    // form.setFieldsValue({
    //   [record.time]: {
    //     items: selectedItems,
    //     // comments: formValues[record.time]?.comments || "",
    //   },
    // });

    // form.setFieldsValue(updatedData);

    setTableData(updatedData);
    onChange(updatedData);
  };

  // const toggleRowVisibility = (time) => {
  //   setHiddenRows((prev) => {
  //     const newSet = new Set(prev);
  //     newSet.add(time); // Hide the row
  //     return newSet;
  //   });
  // };

  const handleQuantityModalOpen = (record) => {
    setModalOpen(true);
    setEditingItem(record);
  };

  const filteredData = tableData;

  const columns = [
    {
      title: "Time",
      dataIndex: "time",
      key: "time",
      width: isMobile ? 88 : 100,
      render: (text) => (
        <Space>
          {/* <Button onClick={() => toggleRowVisibility(text)}>
            <EyeOutlined style={{ color: "red" }} />
          </Button> */}
          {text >= 12
            ? `${text > 12 ? text - 12 : text}:00 PM`
            : `${text}:00 AM`}
        </Space>
      ),
      fixed: isMobile ? undefined : "left",
    },
    {
      title: "Items",
      dataIndex: "items",
      key: "items",
      width: isMobile ? 160 : 220,
      render: (text, record) => (
        <div
          onClick={() => !disabled && handleQuantityModalOpen(record)}
          className="diet-edit-item-cell"
          style={{
            cursor: disabled ? "not-allowed" : "pointer",
            padding: "8px",
            border: "1px solid #d9d9d9",
            borderRadius: "6px",
            minHeight: "32px",
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
            alignItems: "center",
            backgroundColor: "#fafafa",
          }}
        >
          <Form.Item name={[record.time, "items"]} hidden />
          {record?.items?.length > 0 ? (
            record.items.map((item, index) => {
              const dietItem = items?.find((i) => i?.id === item.diet__id);
              const comment = item?.comments;
              const truncatedComment =
                comment && comment.length > 20
                  ? comment.slice(0, 20) + "..."
                  : comment;

              return (
                <Tag
                  color="blue"
                  key={index}
                  className="diet-edit-item-tag"
                  style={{ margin: 0 }}
                >
                  {dietItem?.name} x {item.quantity || 1}
                  {truncatedComment && `, ${truncatedComment}`}
                </Tag>
              );
            })
          ) : (
            <span style={{ color: "#999", fontStyle: "italic" }}>
              Click to add items
            </span>
          )}
        </div>
      ),
    },
    // {
    //   title: "Quantity",
    //   // dataIndex: "quantity",
    //   // key: "quantity",
    //   width: 50,
    //   render: (_, record) =>
    //     record?.items?.length > 0 && (
    //       <Button
    //         onClick={() => {
    //           handleQuantityModalOpen(record);
    //         }}
    //         style={{ border: "none", background: "transparent" }}
    //         icon={<EditIcon />}
    //       />
    //     ),
    // },
    {
      title: "Energy (kcal)",
      dataIndex: "calories",
      key: "calories",
      width: isMobile ? 96 : 110,
    },
    {
      title: "Proteins",
      dataIndex: "proteins",
      key: "proteins",
      width: isMobile ? 80 : 90,
    },

    {
      title: "Carbs",
      dataIndex: "carbohydrates",
      key: "carbohydrates",
      width: isMobile ? 72 : 80,
    },
    {
      title: "Sodium",
      dataIndex: "sodium",
      key: "sodium",
      width: isMobile ? 80 : 90,
    },
    {
      title: "Potassium",
      dataIndex: "potassium",
      key: "potassium",
      width: isMobile ? 88 : 100,
    },

    // {
    //   title: "Comments",
    //   dataIndex: "comments",
    //   key: "comments",
    //   width: 100,
    //   render: (_, record) => (
    //     <Typography.Text>
    //       {record?.items
    //         ?.map((item) => item?.comments?.trim())
    //         .filter((comment) => comment)
    //         .join(", ")}
    //     </Typography.Text>
    //   ),
    // },
  ];

  // const handleItemChange = (selectedItems, record) => {
  //   // const formValues = form.getFieldsValue(); // Get all current form values

  //   const totalValues = selectedItems.reduce(
  //     (totals, itemId) => {
  //       const item = items?.find((dietItem) => dietItem?.id === itemId);
  //       if (item) {
  //         totals.proteins += item?.protien || 0;
  //         totals.calories += item?.calories || 0;
  //         totals.sodium += item?.sodium || 0;
  //         totals.potassium += item?.potassium || 0;
  //         totals.carbohydrates += item?.carbohydrates || 0;
  //       }
  //       return totals;
  //     },
  //     { proteins: 0, calories: 0, sodium: 0, potassium: 0, carbohydrates: 0 }
  //   );

  //   const updatedData = tableData?.map((data) => {
  //     const timeKey = data.time;
  //     const currentFormValue = record || {};
  //     return timeKey === record.time
  //       ? {
  //           ...data,
  //           items: selectedItems?.map((i) => ({
  //             diet__id: i,
  //             quantity: 1,
  //             comments: "",
  //           })),
  //           // comments: currentFormValue.comments || "", // current field's comment
  //           ...totalValues,
  //           modified: true,
  //         }
  //       : {
  //           ...data,
  //           items: data.items,
  //           comments: data.comments,
  //         };
  //   });

  //   // Set updated form values explicitly
  //   // form.setFieldsValue({
  //   //   [record.time]: {
  //   //     items: selectedItems,
  //   //     // comments: formValues[record.time]?.comments || "",
  //   //   },
  //   // });

  //   // form.setFieldsValue(updatedData);

  //   setTableData(updatedData);
  //   onChange(updatedData);
  // };

  const handleFinish = (values) => {
    // Update the table data with new items, quantities and comments
    const updatedTableData = tableData.map((record) => {
      if (record.time === editingItem?.time) {
        // Use the items from the form values (which includes new selections)
        const newItems = values.items || [];

        // Calculate total nutrition values based on new items and quantities
        const totalValues = newItems.reduce(
          (totals, { diet__id, quantity }) => {
            const dietItem = items.find((item) => item.id === diet__id);
            if (dietItem) {
              totals.proteins += (dietItem.protien || 0) * (quantity || 1);
              totals.calories += (dietItem.calories || 0) * (quantity || 1);
              totals.sodium += (dietItem.sodium || 0) * (quantity || 1);
              totals.potassium += (dietItem.potassium || 0) * (quantity || 1);
              totals.carbohydrates +=
                (dietItem.carbohydrates || 0) * (quantity || 1);
            }
            return totals;
          },
          {
            proteins: 0,
            calories: 0,
            sodium: 0,
            potassium: 0,
            carbohydrates: 0,
          }
        );

        return {
          ...record,
          items: newItems,
          ...totalValues,
          modified: true,
        };
      }
      return record;
    });

    // Update the state and trigger onChange callback
    setTableData(updatedTableData);
    onChange(updatedTableData);

    // Close the modal
    setModalOpen(false);
    setEditingItem(null);
    modalForm.resetFields();

    setModalOpen(false);
  };

  const filterOnDietType = (data) => {
    if ((dietDetails?.dietType ?? dietDetails?.ia?.dietType) === "Oral") {
      return Object.fromEntries(
        Object.entries(data).filter(([key]) =>
          ["proteins", "protein", "calories"].includes(key)
        )
      );
    }
    return data;
  };

  const renderNutritionTitle = () => {
    // Use currentModalNutrition when modal is open, otherwise use combinedTotals
    const dietTotals = modalOpen ? currentModalNutrition : combinedTotals;

    const required =
      dietDetails?.nutritional_recommendations ??
      dietDetails?.ia?.nutritional_recommendations;
    // if (!required) {
    //   return <>Diet Items {mapToCustomTag(dietTotals, "purple")}</>;
    // }

    // Create combined ratio tags for energy and proteins
    const combinedTags = [];

    // Energy tag (calories)
    if (
      dietTotals?.calories !== undefined &&
      required?.calories !== undefined
    ) {
      combinedTags.push(
        <Tag key="energy" color="purple">
          Energy - {Math.round(dietTotals?.calories * 10) / 10}/
          {required?.calories} kcal
        </Tag>
      );
    }
    if (dietTotals?.proteins !== undefined && required?.protein !== undefined) {
      combinedTags.push(
        <Tag key="proteins" color="purple">
          Proteins - {Math.round(dietTotals?.proteins * 10) / 10}/
          {required?.protein} g
        </Tag>
      );
    }
    if (
      dietTotals?.carbohydrates !== undefined &&
      required?.carbohydrates !== undefined
    ) {
      combinedTags.push(
        <Tag key="proteins" color="purple" style={{ fontWeight: 600 }}>
          Carbs - {dietTotals?.carbohydrates}/{required?.carbohydrates} g
        </Tag>
      );
    }
    if (dietTotals?.sodium !== undefined && required?.sodium !== undefined) {
      combinedTags.push(
        <Tag key="proteins" color="purple" style={{ fontWeight: 600 }}>
          Sodium - {dietTotals?.sodium}/{required?.sodium} mg
        </Tag>
      );
    }
    if (
      dietTotals?.potassium !== undefined &&
      required?.potassium !== undefined
    ) {
      combinedTags.push(
        <Tag key="proteins" color="purple" style={{ fontWeight: 600 }}>
          Potassium - {dietTotals?.potassium}/{required?.potassium} mg
        </Tag>
      );
    }

    // Proteins tag

    return <>{combinedTags}</>;
  };

  // const mapToCustomTag = (data, color) => {
  //   return Object.entries(data).map(([key, value]) =>
  //     customTag(
  //       key === "calories" ? "energy" : key,
  //       value,
  //       color,
  //       getUnits(key)
  //     )
  //   );
  // };

  const tableScroll = isMobile
    ? { x: 720 }
    : { y: "calc(100vh - 350px)", x: 780 };

  // console.log(filteredData);

  return (
    <>
      <div
        className={`diet-edit-table-wrap${
          isMobile ? " diet-edit-table-wrap--mobile" : ""
        }`}
      >
        <Table
          className="diet-edit-table no-vertical-borders"
          title={() => titleData()}
          dataSource={filteredData}
          columns={columns}
          pagination={false}
          scroll={tableScroll}
        />
      </div>
      <Modal
        className="diet-edit-modal"
        wrapClassName={isMobile ? "diet-edit-modal-wrap" : undefined}
        centered={isMobile}
        title={
          <Typography.Text className="diet-edit-modal-heading">
            Edit Items & Quantity
          </Typography.Text>
        }
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditingItem(null);
          modalForm.resetFields();
        }}
        footer={
          <div className="diet-edit-modal-footer">
            <Button
              key="cancel"
              onClick={() => {
                setModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={() => {
                modalForm.submit();
              }}
            >
              Save
            </Button>
          </div>
        }
        width={isMobile ? "calc(100vw - 16px)" : 700}
        destroyOnClose
      >
        <div className="diet-edit-modal-tags">{renderNutritionTitle()}</div>
        <Form form={modalForm} onFinish={handleFinish} layout="vertical">
          <Form.Item
            name="selectedItems"
            label="Select Items"
            // rules={[
            //   {
            //     required: true,
            //     message: "Please select at least one item",
            //   },
            // ]}
          >
            <Select
              // className={inputStyles.mdSelect}
              style={{ width: "100%" }}
              mode="multiple"
              showSearch
              optionFilterProp="children"
              placeholder="Search and select diet items"
              onChange={(selectedItems) => {
                // Update the form list based on selected items
                const currentItems = modalForm.getFieldValue("items") || [];
                const newItems = selectedItems.map((itemId) => {
                  const existingItem = currentItems.find(
                    (item) => item.diet__id === itemId
                  );
                  return {
                    diet__id: itemId,
                    quantity: existingItem?.quantity || 1,
                    comments: existingItem?.comments || "",
                  };
                });
                modalForm.setFieldsValue({ items: newItems });

                // Recalculate nutrition values for the modal title
                calculateModalNutrition(newItems);
              }}
            >
              {items.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => {
                  const dietId = modalForm.getFieldValue([
                    "items",
                    index,
                    "diet__id",
                  ]);
                  const dietItem = items?.find((item) => item?.id === dietId);
                  // console.log(dietItem);

                  return (
                    <div key={field.key} className="diet-edit-item-card">
                      <Typography.Text
                        strong
                        style={{ display: "block", marginBottom: 8 }}
                      >
                        {dietItem?.name}
                      </Typography.Text>

                      <Form.Item
                        {...field}
                        name={[field.name, "diet__id"]}
                        hidden
                      >
                        <Input type="hidden" />
                      </Form.Item>

                      <div className="diet-edit-item-fields">
                        <Form.Item
                          {...field}
                          name={[field.name, "quantity"]}
                          label="Quantity"
                          rules={[
                            {
                              required: true,
                              message: "Please enter quantity",
                            },
                          ]}
                        >
                         
                          <InputNumber
                            min={1}
                            style={{ width: isMobile ? "100%" : "100px" }}
                            controls={false}
                            onChange={(value) => {
                              // Update nutrition values when quantity changes
                              const currentItems =
                                modalForm.getFieldValue("items") || [];
                              const updatedItems = [...currentItems];
                              if (updatedItems[index]) {
                                updatedItems[index].quantity = value;
                              }
                              modalForm.setFieldsValue({ items: updatedItems });

                              // Recalculate nutrition values for the modal title
                              calculateModalNutrition(updatedItems);
                            }}
                          />
                        </Form.Item>

                        <Form.Item
                          {...field}
                          name={[field.name, "comments"]}
                          label="Comments"
                          className="diet-edit-item-comments"
                        >
                          <Input.TextArea rows={2} />
                        </Form.Item>
                      </div>

                      {/* Display nutrition info for this item */}
                      {dietItem && (
                        <div className="diet-edit-item-nutrition">
                            <span>
                              Energy:{" "}
                              {(
                                dietItem.calories *
                                (modalForm.getFieldValue([
                                  "items",
                                  index,
                                  "quantity",
                                ]) || 1)
                              ).toFixed(1)}{" "}
                              kcal
                            </span>
                            <span>
                              Protein:{" "}
                              {(
                                dietItem.protien *
                                (modalForm.getFieldValue([
                                  "items",
                                  index,
                                  "quantity",
                                ]) || 1)
                              ).toFixed(1)}{" "}
                              g
                            </span>
                            <span>
                              Carbs:{" "}
                              {(
                                dietItem.carbohydrates *
                                (modalForm.getFieldValue([
                                  "items",
                                  index,
                                  "quantity",
                                ]) || 1)
                              ).toFixed(1)}{" "}
                              g
                            </span>
                            <span>
                              Sodium:{" "}
                              {(
                                dietItem.sodium *
                                (modalForm.getFieldValue([
                                  "items",
                                  index,
                                  "quantity",
                                ]) || 1)
                              ).toFixed(1)}{" "}
                              g
                            </span>
                            <span>
                              Potassium:{" "}
                              {(
                                dietItem.potassium *
                                (modalForm.getFieldValue([
                                  "items",
                                  index,
                                  "quantity",
                                ]) || 1)
                              ).toFixed(1)}{" "}
                              g
                            </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </>
  );
};

export default DietEditTable;
